/**
 * CtaStacked — centered headline + CTA over a full-bleed background image
 * with a dark overlay. Cinematic feel; great for chapter-break moments.
 *
 * Registered as: cta / cta-stacked
 */
import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { SectionComponentProps } from "../../registry.js";

gsap.registerPlugin(ScrollTrigger);

interface CtaStackedProps {
  headline?: string;
  subheadline?: string;
  ctaLabel?: string;
  ctaTarget?: string;
  secondaryLabel?: string;
  secondaryTarget?: string;
  imageUrl?: string;
  /** Overlay darkness 0–1 (default 0.55) */
  overlayOpacity?: number;
}

export function CtaStacked({ props }: SectionComponentProps) {
  const {
    headline = "Let's make it happen.",
    subheadline = "Get in touch and let's talk about your project.",
    ctaLabel = "Contact Us",
    ctaTarget = "#quote",
    secondaryLabel,
    secondaryTarget,
    imageUrl,
    overlayOpacity = 0.55,
  } = props as CtaStackedProps;

  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: { trigger: el, start: "top 75%", toggleActions: "play none none none" },
        defaults: { ease: "power3.out" },
      });
      tl.fromTo(".cst-headline", { opacity: 0, y: 36, clipPath: "inset(0 0 100% 0)" },
        { opacity: 1, y: 0, clipPath: "inset(0 0 0% 0)", duration: 0.9 })
        .fromTo(".cst-sub", { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6 }, "-=0.4")
        .fromTo(".cst-actions", { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.5 }, "-=0.3");

      // Subtle parallax on the background image
      if (imageUrl) {
        gsap.fromTo(".cst-bg", { yPercent: -8 }, {
          yPercent: 8, ease: "none",
          scrollTrigger: { trigger: el, start: "top bottom", end: "bottom top", scrub: 2 },
        });
      }
    }, el);

    return () => ctx.revert();
  }, [imageUrl]);

  return (
    <section
      ref={sectionRef}
      id="cta"
      className="relative py-32 px-6 overflow-hidden flex items-center justify-center min-h-[420px]"
      style={!imageUrl ? { background: "var(--color-primary)" } : undefined}
    >
      {imageUrl && (
        <>
          <div
            className="cst-bg absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${imageUrl})`, willChange: "transform" }}
          />
          <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${overlayOpacity})` }} />
        </>
      )}
      {!imageUrl && (
        <>
          <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
          <div className="pointer-events-none absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-white/5" />
        </>
      )}

      <div className="relative z-10 max-w-2xl mx-auto text-center space-y-6">
        <h2 className="cst-headline text-4xl md:text-6xl font-bold text-white leading-tight">
          {headline}
        </h2>
        {subheadline && (
          <p className="cst-sub text-white/75 text-lg leading-relaxed max-w-lg mx-auto">{subheadline}</p>
        )}
        <div className="cst-actions flex flex-col sm:flex-row gap-3 justify-center pt-2">
          <a
            href={ctaTarget}
            className="inline-flex items-center justify-center rounded-[var(--radius)] bg-white text-foreground px-8 py-3 text-sm font-semibold shadow hover:opacity-90 transition-opacity"
          >
            {ctaLabel}
          </a>
          {secondaryLabel && (
            <a
              href={secondaryTarget}
              className="inline-flex items-center justify-center rounded-[var(--radius)] border border-white/30 text-white px-8 py-3 text-sm font-semibold hover:bg-white/10 transition-colors"
            >
              {secondaryLabel}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
